import React, { Component } from 'react';
import * as _ from 'lodash';
import * as PropTypes from 'prop-types';
import Checkbox from '@material-ui/core/Checkbox';
import { getRuleCategoryFriendlyName } from '../../../../services/RuleCategory';
import { RuleTypeLabelPipe } from '../../../../shared/pipes/RuleTypeLabelPipe';
import {
    humanize,
    isKnownParameterType,
    ScenarioParameterType,
    validateParameterValue
} from '../simulation.model';

const reportedTypes = new Set();

/* A type of parameter that this build does not know is not rendered; it is said once in the console, so that it can be found. */
const reportUnknownType = parameter => {
    if (!reportedTypes.has(parameter.type)) {
        reportedTypes.add(parameter.type);
        console.warn(
            `The scenario parameter "${parameter.key}" has the type "${parameter.type}", which is not known here. It is not shown, and the server's default is used.`
        );
    }
};

const describeRange = parameter => {
    const hasMin = !_.isNil(parameter.min);
    const hasMax = !_.isNil(parameter.max);

    if (hasMin && hasMax) {
        return `Between ${parameter.min} and ${parameter.max}`;
    }
    if (hasMin) {
        return `At least ${parameter.min}`;
    }
    if (hasMax) {
        return `At most ${parameter.max}`;
    }
    return null;
};

/* The input for one parameter of a scenario, by the type the catalog gives it. */
export const ScenarioParameterInput = ({ parameter, value, onChange }) => {
    if (!isKnownParameterType(parameter)) {
        reportUnknownType(parameter);
        return null;
    }
    const label = humanize(parameter.key);

    if (parameter.type === ScenarioParameterType.BOOLEAN) {
        return (
            <div className={'sim-field'}>
                <span className={'sim-label'}>{label}</span>
                <div>
                    <Checkbox
                        color={'primary'}
                        style={{ padding: 0 }}
                        checked={!!value}
                        onChange={event => onChange(event.target.checked)}
                    />
                </div>
            </div>
        );
    }

    const error =
        parameter.type === ScenarioParameterType.STRING
            ? null
            : validateParameterValue(parameter, value);
    const range = describeRange(parameter);

    return (
        <div className={'sim-field'}>
            <span className={'sim-label'}>{label}</span>
            <input
                type={'text'}
                className={error ? 'invalid' : ''}
                value={_.isNil(value) ? '' : value}
                onChange={event => onChange(event.target.value)}
            />
            {error ? (
                <span className={'sim-error'}>{error}</span>
            ) : (
                range && <span className={'sim-hint'}>{range}</span>
            )}
        </div>
    );
};

/*
  The scenarios of the catalog, by category, to choose from. A scenario that cannot be played now is shown with the reason. The
  parameters of the ones chosen are shown to be changed.
*/
export class ScenarioPicker extends Component {
    static propTypes = {
        scenarios: PropTypes.array,
        // The values of the parameters of the chosen scenarios, by the id of the scenario
        selected: PropTypes.object,
        onToggle: PropTypes.func,
        onChangeValue: PropTypes.func
    };

    renderScenario = scenario => {
        const { selected, onToggle, onChangeValue } = this.props;
        const isSelected = _.has(selected, scenario.id);
        const available = scenario.available !== false;

        return (
            <div
                key={scenario.id}
                className={
                    'sim-scenario' +
                    (isSelected ? ' selected' : '') +
                    (available ? '' : ' unavailable')
                }
            >
                <div
                    className={'sim-scenario-header'}
                    onClick={() => available && onToggle(scenario.id)}
                >
                    <Checkbox
                        color={'primary'}
                        style={{ padding: 0, marginRight: 15 }}
                        checked={isSelected}
                        disabled={!available}
                    />
                    <div>
                        <div className={'sim-scenario-title'}>
                            {scenario.name}
                        </div>
                        <div className={'sim-scenario-description'}>
                            {scenario.description}
                        </div>
                        <div>
                            {_.map(scenario.ruleTypes, ruleType => (
                                <span key={ruleType} className={'sim-tag'}>
                                    {RuleTypeLabelPipe(ruleType)}
                                </span>
                            ))}
                        </div>
                        {!available && (
                            <div className={'sim-error'}>
                                {scenario.unavailableReason ||
                                    'This scenario cannot be played now.'}
                            </div>
                        )}
                    </div>
                </div>
                {isSelected &&
                    !_.isEmpty(
                        _.filter(scenario.parameters, isKnownParameterType)
                    ) && (
                        <div className={'sim-scenario-parameters sim-form'}>
                            {scenario.parameters.map(parameter => (
                                <ScenarioParameterInput
                                    key={parameter.key}
                                    parameter={parameter}
                                    value={selected[scenario.id][parameter.key]}
                                    onChange={value =>
                                        onChangeValue(
                                            scenario.id,
                                            parameter.key,
                                            value
                                        )
                                    }
                                />
                            ))}
                        </div>
                    )}
            </div>
        );
    };

    render() {
        const groups = _.groupBy(this.props.scenarios, 'category');

        return (
            <div className={'sim-scenario-picker'}>
                {_.map(groups, (scenarios, category) => (
                    <div key={category}>
                        <div className={'sim-category-title'}>
                            {getRuleCategoryFriendlyName(category) || category}
                        </div>
                        {scenarios.map(this.renderScenario)}
                    </div>
                ))}
            </div>
        );
    }
}
